/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { Container } from "react-awesome-styled-grid";
import { Switch } from "react-router-dom";
import styled from "styled-components";
import { theme } from "@nordcloud/gnui";
import { isDev } from "~/config";
import { MenuBar, Header, Footer } from "~/layout";
import { RumRoute as Route } from "~/services/datadog";
import {
  ApiDocsPage,
  AccountsPage,
  AccountDetails,
  Dashboard,
  AccountCreate,
  ResourcesPage,
  ResourceGroupsPage,
  ResourceDetails,
  RolesPage,
  APIKeysPage,
  ResourceGroupCreate,
  ResourceGroupDetails,
  UsersPage,
  ResourceCreate,
  CustomActionsPage,
  CustomActionDetails,
  CustomActionCreate,
  PlansPage,
  PlanCreate,
  PlanDetails,
  NotificationGroupsPage,
  NotificationGroupDetails,
  NotificationGroupsCreate,
  EventsPage,
  EventDetails,
  MappingRulesPage,
  ApplicationsPage,
  ApplicationCreate,
} from "~/views";
import { Reports } from "~/views/resources";
import {
  ResourcesPageV2,
  ResourceDetailsV2,
  ResourceReports,
} from "~/views/resources_v2";
import { ROUTES } from "./routes";

const layoutHorizontalSpacing = theme.spacing.spacing04;

export function GlobalSwitch() {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);

  return (
    <Container
      fluid
      css={{
        flex: 1,
        paddingLeft: 0,
        paddingTop: 0,
        paddingRight: layoutHorizontalSpacing,
      }}
    >
      <ApplicationGrid $isMenuExpanded={isMenuExpanded}>
        <HeaderWrapper>
          <Header />
        </HeaderWrapper>
        <MenuBar onExpandChange={() => setIsMenuExpanded((prev) => !prev)} />
        <ApplicationWrapper>
          <Switch>
            <Route exact path={ROUTES.index} component={Dashboard} />
            <Route exact path={ROUTES.api} component={ApiDocsPage} />
            <Route exact path={ROUTES.events.index} component={EventsPage} />
            <Route
              exact
              path={ROUTES.events.details}
              component={EventDetails}
            />
            <Route
              exact
              path={ROUTES.resources.create}
              component={ResourceCreate}
            />
            <Route
              exact
              path={ROUTES.resources.index}
              component={ResourcesPage}
            />
            <Route
              exact
              path={ROUTES.resourcesV2.index}
              component={ResourcesPageV2}
            />
            <Route exact path={ROUTES.resources.reports} component={Reports} />
            {isDev && (
              <Route exact path="/v2/reports" component={ResourceReports} />
            )}
            <Route
              exact
              path={ROUTES.resources.details}
              component={ResourceDetails}
            />
            <Route
              exact
              path={ROUTES.resourcesV2.details}
              component={ResourceDetailsV2}
            />
            <Route
              exact
              path={ROUTES.accounts.create}
              component={AccountCreate}
            />
            <Route
              exact
              path={ROUTES.accounts.index}
              component={AccountsPage}
            />
            <Route
              exact
              path={ROUTES.accounts.details}
              component={AccountDetails}
            />
            <Route exact path={ROUTES.plans.index} component={PlansPage} />
            <Route exact path={ROUTES.plans.create} component={PlanCreate} />
            <Route exact path={ROUTES.plans.details} component={PlanDetails} />
            <Route
              exact
              path={ROUTES.customActions.create}
              component={CustomActionCreate}
            />
            <Route
              exact
              path={ROUTES.customActions.index}
              component={CustomActionsPage}
            />
            <Route
              exact
              path={ROUTES.customActions.details}
              component={CustomActionDetails}
            />

            <Route
              exact
              path={ROUTES.permissions.roles}
              component={RolesPage}
            />
            <Route
              exact
              path={ROUTES.permissions.apiKeys}
              component={APIKeysPage}
            />
            <Route
              exact
              path={ROUTES.permissions.users}
              component={UsersPage}
            />
            <Route
              exact
              path={ROUTES.resourceGroups.create}
              component={ResourceGroupCreate}
            />
            <Route
              exact
              path={ROUTES.resourceGroups.index}
              component={ResourceGroupsPage}
            />
            <Route
              exact
              path={ROUTES.resourceGroups.details}
              component={ResourceGroupDetails}
            />
            <Route
              exact
              path={ROUTES.notificationGroups.create}
              component={NotificationGroupsCreate}
            />
            <Route
              exact
              path={ROUTES.notificationGroups.index}
              component={NotificationGroupsPage}
            />
            <Route
              exact
              path={ROUTES.notificationGroups.details}
              component={NotificationGroupDetails}
            />
            <Route
              exact
              path={ROUTES.mappingRules.index}
              component={MappingRulesPage}
            />
            {isDev && (
              <>
                <Route
                  exact
                  path={ROUTES.applications.index}
                  component={ApplicationsPage}
                />
                <Route
                  exact
                  path={ROUTES.applications.create}
                  component={ApplicationCreate}
                />
              </>
            )}
          </Switch>
        </ApplicationWrapper>
        <FooterWrapper>
          <Footer />
        </FooterWrapper>
      </ApplicationGrid>
    </Container>
  );
}

// cache menu bar & layout dimensions
const menuWidth = theme.spacing.spacing07;
const expandedMenuWidth = "14rem";
const menuHorizontalPadding = theme.spacing.spacing03;

// `1st calc`: we need to add menuHorizontalPadding twice because we have
// paddingLeft and paddingRight on the menu element

// `2nd calc`: we need to add layoutHorizontalSpacing additionally to mitigate
// issue with right padding on global application container which is wrongly
// interpreted by the grid
const ApplicationGrid = styled.div<{ $isMenuExpanded: boolean }>`
  display: grid;
  gap: ${theme.spacing.spacing04} ${layoutHorizontalSpacing};

  ${({ $isMenuExpanded }) => {
    const width = $isMenuExpanded ? expandedMenuWidth : menuWidth;

    // eslint-disable-next-line sonarjs/no-nested-template-literals
    return `
      grid-template-columns:
        calc(${width} + 2 * ${menuHorizontalPadding})
        calc(
          100% -
            (
              ${width} +
              2 * ${menuHorizontalPadding} +
              ${layoutHorizontalSpacing}
            )
        );
    `;
  }}

  grid-template-areas:
    ". header"
    ". application"
    ". footer";

  width: 100%;
  transition: grid-template-columns 0.3s ease;
`;

const HeaderWrapper = styled.div`
  margin: 0 -${theme.spacing.spacing04} 0 -${layoutHorizontalSpacing};
  grid-area: header;
`;

const ApplicationWrapper = styled.div`
  grid-area: application;
  width: 100%;

  @media screen and (min-width: 2500px) {
    margin: 0 auto;
    max-width: 2500px;
  }
`;

const FooterWrapper = styled.div`
  grid-area: footer;
  padding-top: ${theme.spacing.spacing05};
`;
